import { useState, useEffect, useRef } from "react";
import { ClipboardCheck, NotebookPen, Wind, X, ChevronRight, Flame } from "lucide-react";
import PresenciaBenito from "../PresenciaBenito";
import BenitoAvatar from "../BenitoAvatar";
import { chipsResumenTurno } from "../../lib/registroTurno";
import { getCierreTurnoVisto, setCierreTurnoVisto, fechaHoyKey } from "../../lib/registroTurnoStore";
import { cargarRacha } from "../../lib/turnoRacha";
import { comentarioCierreBenito, comentarioCierreCacheado, cierreBenitoHabilitado } from "../../lib/cierreBenito";
import { trackEvent } from "../../lib/eventTracking";
import { MODULOS } from "../../lib/modulos";

// Cierre del turno — el "cierre del día" del enfermero. Aparece en la zona "Hoy"
// del home cuando el turno terminó y hubo pacientes. Hace dos cosas que antes no
// pasaban: (1) le devuelve la magnitud de su jornada (resumen), y (2) le ofrece
// un cierre humano — descargar el día en Bitácora o tomar un respiro. No es una
// herramienta más: es el gesto de "compañero" que cierra el turno contigo.
//
// Se auto-descarta por el resto del día al cerrarlo (device-local por fecha).
export default function CierreTurno({
  resumen,
  userId,
  onAbrirBitacora,
  onAbrirRespira,
  onAbrirTurno,
  onComentarBenito,      // capa ejercicio: descargar el turno conversando con Benito
  ofreceBitacora = true, // solo la capa formación: al pro no se le empuja la Bitácora
  className = "",
}) {
  const hoy = fechaHoyKey();
  const [oculto, setOculto] = useState(() => getCierreTurnoVisto(userId) === hoy);
  const [racha, setRacha] = useState(0);

  useEffect(() => {
    let vivo = true;
    cargarRacha(userId).then((r) => { if (vivo) setRacha(r?.racha || 0); });
    return () => { vivo = false; };
  }, [userId]);

  // La voz de Benito comentando el turno (pieza 1 de "más toque de IA",
  // 2026-08-01): una llamada Haiku por cierre, cacheada por día. Al modelo
  // solo viajan los hechos agregados del resumen (des-identificados y SOLO
  // logros: la dieta de datos hace imposible la culpa). Si falla, el cierre
  // sigue completo sin la voz, con Reintentar discreto.
  const [comentario, setComentario] = useState(() => comentarioCierreCacheado(userId, hoy));
  const [comentarioCargando, setComentarioCargando] = useState(false);
  const [comentarioError, setComentarioError] = useState(false);
  const comentarioPedido = useRef(false);
  const pedirComentario = (manual = false) => {
    if (comentarioCargando) return;
    setComentarioError(false);
    setComentarioCargando(true);
    if (manual) trackEvent("cierre_benito_reintento", { modulo: MODULOS.MI_TURNO });
    comentarioCierreBenito({ resumen, userId, fecha: hoy })
      .then((t) => { setComentario(t); trackEvent("cierre_benito_comentario", { modulo: MODULOS.MI_TURNO }); })
      .catch(() => setComentarioError(true))
      .finally(() => setComentarioCargando(false));
  };
  useEffect(() => {
    // Kill-switch (auditoría 2026-08-01): es la única llamada IA que se
    // dispara sin toque del usuario, así que su interruptor es obligatorio.
    if (!cierreBenitoHabilitado()) return;
    if (oculto || comentario || comentarioPedido.current) return;
    if (!resumen || resumen.totalPacientes === 0) return;
    comentarioPedido.current = true;
    pedirComentario();
    // Una vez por montaje del cierre; el caché por día evita re-generar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (oculto || !resumen || resumen.totalPacientes === 0) return null;

  const chips = chipsResumenTurno(resumen);
  const descartar = () => { setCierreTurnoVisto(userId, hoy); setOculto(true); };

  return (
    <div className={`rounded-tk-md border border-edge bg-surface p-5 ${className}`}>
      <div className="flex items-start gap-3">
        <ClipboardCheck size={20} strokeWidth={1.7} className="text-module-prepara-turno flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-micro uppercase tracking-wide text-ink-muted font-sans">Cierre del turno</p>
          <p className="mt-0.5 text-body font-semibold text-ink font-sans">Terminaste. Esto sostuviste hoy.</p>
          {racha >= 2 && (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-clinical-attention-bg text-clinical-attention text-micro font-semibold font-sans px-2 py-0.5">
              <Flame size={12} strokeWidth={2} className="flex-shrink-0" />
              Racha de {racha} turnos
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={descartar}
          aria-label="Cerrar"
          className="flex-shrink-0 -mt-1 -mr-1 p-1 text-ink-faint hover:text-ink-muted transition-colors"
        >
          <X size={16} strokeWidth={2} />
        </button>
      </div>

      {/* Resumen de la jornada */}
      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {chips.map((c) => (
            <span
              key={c}
              className="inline-flex items-center text-small font-sans text-ink bg-paper border border-edge rounded-full px-2.5 py-1"
            >
              {c}
            </span>
          ))}
        </div>
      )}

      {/* Tu turno en números (dictado del fundador 31-jul): hechos registrados
          que objetivan la jornada — solo lee del resumen agregado (este
          componente sigue sin ver registros crudos). Anti-culpa: constata,
          jamás acusa; la cifra de minutos ahorrados espera el dato del piloto. */}
      {(resumen.evolucionesCompletas > 0 || Number.isFinite(resumen.examenMinutos)) && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {resumen.evolucionesCompletas > 0 && (
            <div className="rounded-tk-sm bg-paper border border-edge px-2 py-2 text-center">
              <p className="text-body font-bold text-ink font-sans leading-none tabular-nums">{resumen.evolucionesCompletas}/{resumen.totalPacientes}</p>
              <p className="text-micro text-ink-muted font-sans mt-1 leading-tight">{resumen.evolucionesCompletas === 1 ? "evolución completa" : "evoluciones completas"}</p>
            </div>
          )}
          {Number.isFinite(resumen.examenMinutos) && (
            <div className="rounded-tk-sm bg-paper border border-edge px-2 py-2 text-center">
              <p className="text-body font-bold text-ink font-sans leading-none tabular-nums">{resumen.examenMinutos} min</p>
              <p className="text-micro text-ink-muted font-sans mt-1 leading-tight">de recibir al paciente al examen</p>
            </div>
          )}
        </div>
      )}

      {/* La voz de Benito: el comentario del turno, entre los números y el
          cierre humano. Nunca bloquea (si falla, Reintentar discreto). */}
      {comentario && (
        <div className="mt-3 flex items-start gap-2.5 rounded-tk-sm border border-accent-border bg-accent-bg/40 px-3 py-2.5 fade-up">
          <span className="benito-asoma flex-shrink-0 grid place-items-center w-8 h-8 rounded-full bg-paper border border-edge/60 overflow-hidden"><BenitoAvatar size={27} /></span>
          <p className="text-small text-ink font-sans leading-relaxed">{comentario}</p>
        </div>
      )}
      {!comentario && comentarioCargando && (
        <div className="mt-3 flex items-center gap-2.5 px-1" aria-live="polite">
          <PresenciaBenito estado="pensando" size={16} className="text-accent flex-shrink-0" />
          <p className="text-micro text-ink-muted font-sans">Benito está mirando tu turno…</p>
        </div>
      )}
      {!comentario && !comentarioCargando && comentarioError && (
        <button
          type="button"
          onClick={() => pedirComentario(true)}
          className="mt-3 flex items-center gap-2 px-1 text-micro text-ink-muted font-sans hover:text-accent transition-colors"
        >
          <PresenciaBenito estado="reposo" size={16} className="flex-shrink-0" />
          Benito no alcanzó a comentar tu turno. Reintentar
        </button>
      )}

      {/* Cierre humano. Para la capa formación, reflexionar el turno es su gesto
          primario del cierre (el diario de campo del internado): va como acción
          principal, con el respiro como salida secundaria. Al pro no se le
          ofrece la Bitácora (feedback real) — solo el respiro. */}
      <div className="mt-4 pt-4 border-t border-edge">
        {ofreceBitacora ? (
          <>
            <p className="text-small text-ink-subtle font-sans">¿Cómo estuvo tu turno? Bájalo mientras está fresco.</p>
            <button
              type="button"
              onClick={() => { trackEvent("cierre_turno_a_bitacora", { modulo: MODULOS.MI_TURNO }); onAbrirBitacora?.(); }}
              className="mt-3 w-full flex items-center gap-2 text-left rounded-tk-sm border border-accent-border bg-accent-bg/40 px-3 py-2.5 hover:bg-accent-bg/60 transition-colors"
            >
              <NotebookPen size={16} strokeWidth={1.7} className="text-module-bitacora flex-shrink-0" />
              <span className="text-small font-medium text-ink font-sans">Reflexiona tu turno</span>
            </button>
            <button
              type="button"
              onClick={onAbrirRespira}
              className="mt-2 w-full flex items-center justify-center gap-1.5 text-small font-sans text-ink-muted hover:text-ink transition-colors"
            >
              <Wind size={15} strokeWidth={1.7} className="flex-shrink-0" />
              <span>O toma un respiro</span>
            </button>
          </>
        ) : (
          <>
            <p className="text-small text-ink-subtle font-sans">¿Cómo vienes del turno?</p>
            {/* La capa ejercicio no lleva Bitácora: su cierre humano es Benito,
                que además YA SABE cómo estuvo el turno (contexto vivo: camas,
                evoluciones, fase). Prefill — el usuario decide qué contar. */}
            {onComentarBenito && (
              <button
                type="button"
                onClick={() => { trackEvent("cierre_turno_a_benito", { modulo: MODULOS.MI_TURNO }); onComentarBenito(); }}
                className="mt-3 w-full flex items-center gap-2 text-left rounded-tk-sm border border-accent-border bg-accent-bg/40 px-3 py-2.5 hover:bg-accent-bg/60 transition-colors"
              >
                <span className="benito-asoma flex-shrink-0 grid place-items-center w-7 h-7 rounded-full bg-paper border border-edge/60 overflow-hidden"><BenitoAvatar size={24} /></span>
                <span className="text-small font-medium text-ink font-sans">Coméntale tu turno a Benito</span>
              </button>
            )}
            <button
              type="button"
              onClick={onAbrirRespira}
              className={`${onComentarBenito ? "mt-2 justify-center" : "mt-3"} w-full flex items-center gap-2 text-left rounded-tk-sm border border-edge bg-paper px-3 py-2.5 hover:bg-surface transition-colors`}
            >
              <Wind size={16} strokeWidth={1.7} className="text-module-respira flex-shrink-0" />
              <span className="text-small font-medium text-ink font-sans">Toma un respiro</span>
            </button>
          </>
        )}

        {onAbrirTurno && (
          <button
            type="button"
            onClick={onAbrirTurno}
            className="mt-2 w-full flex items-center gap-1.5 text-small font-sans text-ink-muted hover:text-ink transition-colors"
          >
            <span>Ver mi turno y entrega</span>
            <ChevronRight size={14} strokeWidth={1.8} className="flex-shrink-0" />
          </button>
        )}
      </div>
    </div>
  );
}
